import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

const EnrollRevaluation = () => {
  const [feeHeads, setFeeHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [student, setStudent] = useState(undefined);
  const [studentLoading, setStudentLoading] = useState(true);
  const [subjectsDialogOpen, setSubjectsDialogOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [currentFeeHead, setCurrentFeeHead] = useState(null);
  // Map: fee_head_id -> array of selected subjects
  const [selectedSubjectsMap, setSelectedSubjectsMap] = useState({});
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user") || "null");
    if (!user?.client_id || !user?.user_email) return;
    setStudentLoading(true);
    const fetchStudent = async () => {
      try {
        const url = `${API_BASE_URL}/api/students/by-email/${user.client_id}/${encodeURIComponent(
          user.user_email
        )}`;
        const res = await axios.get(url);
        console.log(res);
        const data = res.data?.data || res.data || null;
        setStudent((prev) => {
          // Only update if student_id actually changes
          if ((prev && data && prev.student_id === data.student_id) || (!prev && !data)) {
            return prev;
          }
          return data && data.student_id ? data : null;
        });
      } catch (err) {
        setStudent(null);
      } finally {
        setStudentLoading(false);
      }
    };
    fetchStudent();
  }, []);

  useEffect(() => {
    if (!student || student === null) return;
    const fetchFeeHeads = async () => {
      try {
        setLoading(true);
        setError(null);
        const user = JSON.parse(sessionStorage.getItem("user") || "null");
        const clientId = user?.client_id || 1;
        const collegeId = user?.college_id;

        const res = await axios.get(
          `${API_BASE_URL}/api/enrollrevaluation/active-fee-heads/${clientId}/${collegeId}`
        );
        if (res.data && res.data.success) {
          console.log("[EnrollRevaluation] Fee Heads Response:", res.data.data);
          setFeeHeads(res.data.data);
        } else {
          setError("Failed to fetch fee heads");
          setSnackbarMessage("Failed to fetch fee heads");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching fee heads");
        setSnackbarMessage(err.response?.data?.message || "Error fetching fee heads");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchFeeHeads();
  }, [student]);

  // Track applied fee_head_ids from FeeBookExamList
  const [appliedFeeHeadIds, setAppliedFeeHeadIds] = useState([]);

  // Callback to update appliedFeeHeadIds from FeeBookExamList
  const handleAppliedFeeHeadIdsUpdate = (ids) => {
    setAppliedFeeHeadIds(ids);
  };

  const columns = [
    {
      field: "sno",
      headerName: "SNo",
      width: 80,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },

    {
      field: "fee_head_name",
      headerName: "Fee Head Name",
      width: 500,
      renderCell: (params) => (
        <span style={{ whiteSpace: "normal", wordBreak: "break-all" }} title={params.value}>
          {params.value}
        </span>
      ),
    },
    {
      field: "select_subjects",
      headerName: "Select Courses",
      width: 180,
      sortable: false,
      renderCell: (params) =>
        appliedFeeHeadIds.includes(params.row.fee_head_id) ? (
          <Button variant="outlined" size="small" disabled sx={{ color: "#888" }}>
            Already applied
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={() => handleOpenSubjectsDialog(params.row)}
            sx={{ color: "#fff" }}
          >
            Select Courses
          </Button>
        ),
    },
    {
      field: "fee_amount",
      headerName: "Fee Amount",
      width: 130,
      valueGetter: (params) => {
        const selected = selectedSubjectsMap[params.row.fee_head_id] || [];
        if (!selected.length) return "";
        // Use fee_amount from the row (from tbl_exam_valuation_schedule_master)
        const feeAmount = params.row.fee_amount || 0;
        return selected.length * feeAmount;
      },
    },
    {
      field: "apply",
      headerName: "Action",
      width: 220,
      sortable: false,
      renderCell: (params) =>
        appliedFeeHeadIds.includes(params.row.fee_head_id) ? (
          <Button variant="outlined" size="small" disabled sx={{ color: "#888" }}>
            Already applied
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={() => handleApply(params.row)}
            sx={{ color: "#da1818" }}
          >
            Confirm Revaluation
          </Button>
        ),
    },
  ];

  // For dialog: use local state for selected subjects, but update map on close
  const [dialogSelectedSubjects, setDialogSelectedSubjects] = useState([]);
  const handleOpenSubjectsDialog = async (feeHead) => {
    setCurrentFeeHead(feeHead);
    setSubjectsDialogOpen(true);
    setSubjects([]);
    setDialogSelectedSubjects(selectedSubjectsMap[feeHead.fee_head_id] || []);
    setSubjectsLoading(true);
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "null");
      const clientId = user?.client_id || 1;
      const student_rollno = student?.student_rollno || "";
      const res = await axios.get(`${API_BASE_URL}/api/enrollrevaluation/subjects`, {
        params: {
          clientId,
          fee_head_name: feeHead.fee_head_name,
          student_rollno,
        },
      });
      if (res.data && res.data.success) {
        setSubjects(res.data.data);
      } else {
        setSnackbarMessage("Failed to fetch subjects");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Error fetching subjects");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSubjectsLoading(false);
    }
  };

  const handleCloseSubjectsDialog = (save = false) => {
    setSubjectsDialogOpen(false);
    setSubjects([]);
    if (save && currentFeeHead) {
      setSelectedSubjectsMap((prev) => ({
        ...prev,
        [currentFeeHead.fee_head_id]: dialogSelectedSubjects,
      }));
    }
    setDialogSelectedSubjects([]);
    setCurrentFeeHead(null);
  };

  const handleSubjectToggle = (subject) => {
    setDialogSelectedSubjects((prev) =>
      prev.includes(subject) ? prev.filter((s) => s !== subject) : [...prev, subject]
    );
  };

  // Handle Apply button
  const handleApply = async (feeHead) => {
    const selected = selectedSubjectsMap[feeHead.fee_head_id] || [];
    if (!selected.length) {
      setSnackbarMessage("Please select at least one subject.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    try {
      // Try to get year_number and semester_number from the feeHead row, or from the first subject if available
      let year_number = feeHead.year_number || null;
      let semester_number = feeHead.semester_number || null;
      // If not present, try to infer from subjects (if your backend/subject API provides it)
      // Otherwise, keep as null
      const payload = {
        fee_head_id: feeHead.fee_head_id,
        student_id: student.student_id,
        student_rollno: student.student_rollno,
        college_id: student.college_id,
        semester_number,
        year_number,
        fee_amount: selected.length * (feeHead.fee_amount || 0),
        remarks: selected.join(","),
        workflow_status: "DRAFT",
        created_by: student.student_rollno,
      };
      console.log(payload);
      await axios.post(`${API_BASE_URL}/api/enrollrevaluation/apply-fee-book`, payload);
      setSnackbarMessage("Fee book entry applied successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (refreshAllData) refreshAllData();
      // Optionally, refresh appliedFeeHeadIds or feeHeads here
      // Show message dialog/modal if needed
    } catch (err) {
      let msg = err.response?.data?.message || "Error applying fee book entry.";
      if (msg.includes("already applied")) {
        msg = "Fee book entry already applied.";
      }
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // --- Add this function to fetch both fee heads and fee book entries ---
  const refreshAllData = async () => {
    // Fetch fee heads
    const user = JSON.parse(sessionStorage.getItem("user") || "null");
    const clientId = user?.client_id || 1;
    const collegeId = user?.college_id || 1;
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(
        `${API_BASE_URL}/api/enrollrevaluation/active-fee-heads/${clientId}${collegeId}`
      );
      if (res.data && res.data.success) {
        setFeeHeads(res.data.data);
      } else {
        setError("Failed to fetch fee heads");
        setSnackbarMessage("Failed to fetch fee heads");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching fee heads");
      setSnackbarMessage(err.response?.data?.message || "Error fetching fee heads");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
    // FeeBookExamList will refresh its own data when student changes (triggered by prop)
    setStudent((prev) => ({ ...prev })); // force rerender to trigger FeeBookExamList useEffect
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Paper elevation={3} sx={{ p: 2 }}>
          <Toolbar>
            <Typography variant="h6" component="div">
              Active Fee Heads for Revaluation
            </Typography>
          </Toolbar>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : feeHeads.length === 0 ? (
            <Alert severity="info">No active fee heads available for today.</Alert>
          ) : (
            <Box sx={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={feeHeads.map((fh) => ({ ...fh, id: fh.fee_head_id }))}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10]}
                disableSelectionOnClick
              />
            </Box>
          )}

          {/* Dialog should be outside the conditional rendering but inside the Paper */}
          <Dialog
            open={subjectsDialogOpen}
            onClose={() => handleCloseSubjectsDialog(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Select Courses for Fee Head</DialogTitle>
            <DialogContent>
              {subjectsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 100,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : subjects.length === 0 ? (
                <Alert severity="info">No subjects found for this fee head.</Alert>
              ) : (
                <List>
                  {subjects.map((subject) => {
                    let code = subject;
                    let name = "";
                    if (typeof subject === "object" && subject !== null) {
                      code = subject.course_code || subject.code || subject.subject_code || "";
                      name = subject.course_name || subject.name || subject.subject_name || "";
                    } else if (typeof subject === "string") {
                      const parts = subject.split(" - ");
                      code = parts[0];
                      name = parts[1] || "";
                    }
                    const displayText = name ? `${code} - ${name}` : code;
                    return (
                      <ListItem
                        key={code}
                        onClick={() => handleSubjectToggle(code)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={dialogSelectedSubjects.includes(code)}
                            tabIndex={-1}
                            disableRipple
                            sx={{
                              color: "#000000",
                              "&.Mui-checked": {
                                color: "#000000",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 24,
                                border: "2px solid #000000",
                                borderRadius: "4px",
                                backgroundColor: dialogSelectedSubjects.includes(code)
                                  ? "#000000"
                                  : "transparent",
                              },
                              "& .MuiSvgIcon-root path": {
                                fill: dialogSelectedSubjects.includes(code)
                                  ? "#ffffff"
                                  : "transparent",
                              },
                            }}
                            inputProps={{ "aria-labelledby": `checkbox-list-label-${code}` }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          id={`checkbox-list-label-${code}`}
                          primary={displayText}
                          primaryTypographyProps={{ fontWeight: 500, color: "#1a1515" }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleCloseSubjectsDialog(false)}>CLOSE</Button>
              <Button
                onClick={() => handleCloseSubjectsDialog(true)}
                variant="contained"
                color="primary"
                style={{ color: "#fff" }}
              >
                SAVE
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>

        <Paper elevation={2} sx={{ p: 2, mt: 4 }}>
          <Toolbar>
            <Typography variant="h6" component="div">
              Fee Book Entries (Exam Fees)
            </Typography>
          </Toolbar>
          <FeeBookExamList
            student={student}
            onAppliedFeeHeadIdsUpdate={handleAppliedFeeHeadIdsUpdate}
            feeHeads={feeHeads}
            refreshAllData={refreshAllData}
          />
        </Paper>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

FeeBookExamList.propTypes = {
  student: PropTypes.shape({
    student_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    student_rollno: PropTypes.string,
    college_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onAppliedFeeHeadIdsUpdate: PropTypes.func,
  feeHeads: PropTypes.array,
  refreshAllData: PropTypes.func,
};

function FeeBookExamList({ student, onAppliedFeeHeadIdsUpdate, feeHeads, refreshAllData }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  useEffect(() => {
    if (!student?.student_id) return;
    const fetchFeeBook = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/enrollrevaluation/fee-books/by-student/${student.student_id}`
        );
        let data = res.data?.items || res.data?.data || [];
        data = data.filter((row) => {
          const name = row.fee_head_name || row.fee_head?.fee_head_name || "";
          return name.trim().toUpperCase().endsWith("EXAMFEE");
        });
        const mappedRows = data.map((r) => ({
          ...r,
          fee_head_name: r.fee_head_name || r.fee_head?.fee_head_name || "",
          id: r.fee_book_id,
        }));
        setRows(mappedRows);
        if (onAppliedFeeHeadIdsUpdate) {
          const ids = mappedRows.map((r) => r.fee_head_id);
          onAppliedFeeHeadIdsUpdate(ids);
        }
      } catch (err) {
        alert(err.message);
        setError(err.response?.data?.message, "Failed to fetched fee book data");
      } finally {
        setLoading(false);
      }
    };
    fetchFeeBook();
  }, [student]);

  const [menuAnchorEls, setMenuAnchorEls] = useState({});

  const handleMenuOpen = (event, id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: event.currentTarget }));
  };
  const handleMenuClose = (id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: null }));
  };
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const handleEdit = async (row) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/enrollrevaluation/fee-receipt/exists`, {
        params: {
          fee_head_name: row.fee_head_name,
          student_rollno: row.student_rollno,
        },
      });
      if (res.data && res.data.exists) {
        setSnackbarMessage("Cannot edit: Receipt already exists for this fee head and student.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        handleMenuClose(row.fee_book_id);
        return;
      }
    } catch (err) {
      setSnackbarMessage("Error checking receipt existence.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      handleMenuClose(row.fee_book_id);
      return;
    }
    setEditForm({ ...row });
    setEditDialogOpen(true);
    handleMenuClose(row.fee_book_id);
  };
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditForm(null);
  };
  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };
  const [editSubjectsDialogOpen, setEditSubjectsDialogOpen] = useState(false);
  const [editSubjects, setEditSubjects] = useState([]);
  const [editSubjectsLoading, setEditSubjectsLoading] = useState(false);
  const [editSelectedSubjects, setEditSelectedSubjects] = useState([]);

  const handleEditSubjects = async (editRow) => {
    setEditSubjectsDialogOpen(true);
    setEditSubjects([]);
    setEditSubjectsLoading(true);
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "null");
      const clientId = user?.client_id || 1;
      const student_rollno = student?.student_rollno || "";
      const res = await axios.get(`${API_BASE_URL}/api/enrollrevaluation/subjects`, {
        params: {
          clientId,
          fee_head_name: editRow.fee_head_name,
          student_rollno,
        },
      });
      if (res.data && res.data.success) {
        setEditSubjects(res.data.data);
      } else {
        setSnackbarMessage("Failed to fetch subjects");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Error fetching subjects");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setEditSubjectsLoading(false);
    }
  };

  const handleEditSubjectToggle = (subject) => {
    setEditSelectedSubjects((prev) =>
      prev.includes(subject) ? prev.filter((s) => s !== subject) : [...prev, subject]
    );
  };

  const handleEditSubjectsDialogClose = (save = false) => {
    setEditSubjectsDialogOpen(false);
    if (save && editForm) {
      // Find the correct fee_amount from tbl_exam_valuation_schedule_master (feeHeads)
      const feeHead = feeHeads.find((fh) => fh.fee_head_id === editForm.fee_head_id);
      const perSubjectFee =
        feeHead && typeof feeHead.fee_amount === "number"
          ? feeHead.fee_amount
          : parseFloat(feeHead?.fee_amount) || 0;
      setEditForm((prev) => ({
        ...prev,
        remarks: editSelectedSubjects.join(","),
        fee_amount: editSelectedSubjects.length * perSubjectFee,
      }));
    }
  };

  const handleEditFormSubmit = async () => {
    if (!editForm) return;
    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/enrollrevaluation/fee-books/${editForm.fee_book_id}`,
        editForm
      );
      console.log("Edit response:", res.data);
      if (res.data && res.data.success) {
        setSnackbarMessage("Fee book entry edited successfully.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setEditDialogOpen(false);
        setEditForm(null);
        if (refreshAllData) refreshAllData();
      } else {
        let msg = res.data?.message || "Error editing fee book entry.";
        setSnackbarMessage(msg);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Edit error:", err);
      let msg = err.response?.data?.message || "Error editing fee book entry.";
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async (row) => {
    try {
      await axios.get(`${API_BASE_URL}/api/enrollrevaluation/fee-receipt/exists`, {
        params: {
          fee_head_name: row.fee_head_name,
          student_rollno: row.student_rollno,
        },
      });
    } catch (err) {
      setSnackbarMessage("Error checking receipt existence.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/enrollrevaluation/fee-books/${row.fee_book_id}`
      );
      if (response.data && response.data.success) {
        setSnackbarMessage("Fee book entry deleted successfully.");
        setSnackbarSeverity("success");
        if (refreshAllData) refreshAllData();
      } else {
        setSnackbarMessage(response.data?.message || "Error deleting fee book entry.");
        setSnackbarSeverity("error");
      }
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Error deleting fee book entry.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    handleMenuClose(row.fee_book_id);
  };

  const columns = [
    {
      field: "sno",
      headerName: "SNo",
      width: 80,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },

    { field: "fee_head_name", headerName: "Fee Head Name", flex: 1.5, minWidth: 350 },
    { field: "fee_amount", headerName: "Amount", flex: 1, minWidth: 40 },
    { field: "remarks", headerName: "Course Codes", flex: 1.5, minWidth: 350 },

    {
      field: "created_on",
      headerName: "Created On",
      flex: 1,
      minWidth: 120,
      valueGetter: (p) => p.row.created_on?.slice(0, 10),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row.fee_book_id)}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEls[params.row.fee_book_id]}
            open={Boolean(menuAnchorEls[params.row.fee_book_id])}
            onClose={() => handleMenuClose(params.row.fee_book_id)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={() => handleEdit(params.row)}>Edit</MenuItem>
            <MenuItem onClick={() => handleDelete(params.row)}>Delete</MenuItem>
          </Menu>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      {loading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            disableSelectionOnClick
          />
          {/* Edit Fee Book Dialog */}
          <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Fee Book Entry</DialogTitle>
            <DialogContent>
              {editForm && (
                <Box
                  component="form"
                  sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <TextField
                      label="Fee Head Name"
                      value={editForm.fee_head_name}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={() => {
                        setEditSelectedSubjects(
                          editForm.remarks ? editForm.remarks.split(",") : []
                        );
                        handleEditSubjects(editForm);
                      }}
                      sx={{ color: "#fff", height: 40 }}
                    >
                      Select Subjects
                    </Button>
                    <TextField
                      label="Amount"
                      value={editForm.fee_amount}
                      InputProps={{ readOnly: true }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <TextField
                    label="Remarks"
                    value={editForm.remarks || ""}
                    onChange={(e) => handleEditFormChange("remarks", e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditDialogClose}>CANCEL</Button>
              <Button
                variant="contained"
                onClick={handleEditFormSubmit}
                color="primary"
                sx={{ color: "#fff" }}
              >
                UPDATE
              </Button>
            </DialogActions>
          </Dialog>
          {/* Edit Subjects Dialog */}
          <Dialog
            open={editSubjectsDialogOpen}
            onClose={() => handleEditSubjectsDialogClose(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Select Subjects for Fee Head</DialogTitle>
            <DialogContent>
              {editSubjectsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 100,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : editSubjects.length === 0 ? (
                <Alert severity="info">No subjects found for this fee head.</Alert>
              ) : (
                <List>
                  {editSubjects.map((subject) => {
                    let code = subject;
                    let name = "";
                    if (typeof subject === "object" && subject !== null) {
                      code = subject.course_code || subject.code || subject.subject_code || "";
                      name = subject.course_name || subject.name || subject.subject_name || "";
                    } else if (typeof subject === "string") {
                      const parts = subject.split(" - ");
                      code = parts[0];
                      name = parts[1] || "";
                    }
                    const displayText = name ? `${code} - ${name}` : code;
                    return (
                      <ListItem
                        key={code}
                        onClick={() => handleEditSubjectToggle(code)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={editSelectedSubjects.includes(code)}
                            tabIndex={-1}
                            disableRipple
                            sx={{
                              color: "#000000",
                              "&.Mui-checked": {
                                color: "#000000",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 24,
                                border: "2px solid #000000",
                                borderRadius: "4px",
                                backgroundColor: editSelectedSubjects.includes(code)
                                  ? "#000000"
                                  : "transparent",
                              },
                              "& .MuiSvgIcon-root path": {
                                fill: editSelectedSubjects.includes(code)
                                  ? "#ffffff"
                                  : "transparent",
                              },
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={displayText} />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleEditSubjectsDialogClose(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => handleEditSubjectsDialogClose(true)}
                color="primary"
                sx={{ color: "#fff" }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EnrollRevaluation;
